(() => {
  const elementName = 'pbn-main-options';
  if (customElements.get(elementName)) return;

  class PbnMainOptions extends HTMLElement {
    static isOptionAvailable(variants, values, index, role, formatIndex) {
      return variants.some(variant => {
        if (!variant.available || variant.options[index] !== values[index]) return false;
        if (role === 'format') return true;
        if (role === 'size') return formatIndex < 0 || variant.options[formatIndex] === values[formatIndex];
        return values.every((value, position) => variant.options[position] === value);
      });
    }

    static resolveVariant(variants, values, index, role, formatIndex, sizeIndex) {
      const exact = variants.find(variant => variant.available && values.every((value, position) => variant.options[position] === value));
      if (exact) return exact;
      if (role !== 'format' && role !== 'size') return undefined;
      const candidates = variants.filter(variant => variant.available && variant.options[index] === values[index] &&
        (role !== 'size' || formatIndex < 0 || variant.options[formatIndex] === values[formatIndex]));
      const score = variant => variant.options.reduce((total, value, position) =>
        total + (value === values[position] ? (position === sizeIndex ? 10 : 1) : 0), 0);
      return candidates.sort((a, b) => score(b) - score(a))[0];
    }

    connectedCallback() {
      if (this.controller) return;

      const select = this.querySelector('[data-pbn-main-variant-select]');
      const variantsNode = this.querySelector('[data-pbn-main-variants]');
      if (!(select instanceof HTMLSelectElement) || !variantsNode) return;

      let variants;
      try {
        variants = JSON.parse(variantsNode.textContent || '[]');
      } catch (error) {
        return;
      }
      if (!Array.isArray(variants) || !variants.length) return;

      this.controller = new AbortController();
      this.inputModality = 'pointer';
      this.dataset.pbnMainInputModality = 'pointer';
      const { signal } = this.controller;
      const radios = Array.from(this.querySelectorAll('[data-pbn-main-option-index]'));
      const dialogs = Array.from(this.querySelectorAll('.pbn-main-options-dialog'));
      const formatIndex = Number(this.dataset.pbnMainFormatIndex);
      const sizeIndex = Number(this.dataset.pbnMainSizeIndex);
      const currentVariant = () => variants.find(variant => String(variant.id) === select.value);
      const selectedValues = () => (currentVariant()?.options || []).slice();

      const rememberKeyboard = () => {
        this.inputModality = 'keyboard';
        this.dataset.pbnMainInputModality = 'keyboard';
      };
      const rememberPointer = () => {
        this.inputModality = 'pointer';
        this.dataset.pbnMainInputModality = 'pointer';
      };
      document.addEventListener('keydown', rememberKeyboard, { capture: true, signal });
      document.addEventListener('pointerdown', rememberPointer, { capture: true, signal });
      document.addEventListener('touchstart', rememberPointer, { capture: true, passive: true, signal });

      const updateAvailability = () => {
        const values = selectedValues();
        radios.forEach(radio => {
          const index = Number(radio.dataset.pbnMainOptionIndex);
          const candidate = values.slice();
          candidate[index] = radio.value;
          const available = PbnMainOptions.isOptionAvailable(variants, candidate, index, radio.dataset.pbnMainOptionRole, formatIndex);
          radio.disabled = !available;
          radio.closest('.pbn-main-options__card')?.classList.toggle('is-unavailable', !available);
          radio.closest('.pbn-main-options__card')?.setAttribute('title', available ? '' : 'Unavailable for the selected format and size');
        });
      };

      const selectVariant = event => {
        const values = selectedValues();
        const radio = event.currentTarget;
        const index = Number(radio.dataset.pbnMainOptionIndex);
        values[index] = radio.value;
        const variant = PbnMainOptions.resolveVariant(variants, values, index, radio.dataset.pbnMainOptionRole, formatIndex, sizeIndex);
        if (!variant) {
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        select.value = String(variant.id);
        select.dispatchEvent(new Event('change', { bubbles: true }));
        updateAvailability();
        const notice = this.querySelector('[data-pbn-main-notice]');
        if (notice) notice.textContent = values.some((value, position) => variant.options[position] !== value) ?
          'Selection updated to an available combination.' : '';

        if (this.inputModality === 'pointer' && event.currentTarget instanceof HTMLElement) {
          const changedRadio = event.currentTarget;
          requestAnimationFrame(() => changedRadio.blur());
        }
      };

      radios.forEach(radio => radio.addEventListener('change', selectVariant, { signal }));

      select.addEventListener('change', () => {
        const variant = variants.find(candidate => String(candidate.id) === select.value);
        variant?.options?.slice(0, 3).forEach((value, index) => {
          const radio = radios.find(candidate => Number(candidate.dataset.pbnMainOptionIndex) === index && candidate.value === value);
          if (radio) radio.checked = true;
        });
        updateAvailability();
      }, { signal });

      this.querySelectorAll('[data-pbn-main-dialog-open]').forEach(button => {
        button.addEventListener('click', event => {
          const dialog = document.getElementById(button.dataset.pbnMainDialogOpen);
          if (!(dialog instanceof HTMLDialogElement)) return;

          const modality = event.detail === 0 ? 'keyboard' : this.inputModality;
          dialog.__pbnOpener = button;
          dialog.__pbnOpenModality = modality;
          dialog.__pbnCloseModality = modality;
          this.lockPageScroll();
          window.MyArtModalMotion ? window.MyArtModalMotion.open(dialog) : dialog.showModal();
          dialog.querySelector('[data-pbn-main-dialog-surface]')?.scrollTo?.(0, 0);

          requestAnimationFrame(() => {
            if (!dialog.open) return;
            if (modality === 'keyboard') {
              const visibleClose = Array.from(dialog.querySelectorAll('[data-pbn-main-dialog-close]'))
                .find(control => control instanceof HTMLElement && getComputedStyle(control).display !== 'none');
              visibleClose?.focus({ preventScroll: true });
              return;
            }

            const surface = dialog.querySelector('[data-pbn-main-dialog-surface]');
            if (surface instanceof HTMLElement) surface.focus({ preventScroll: true });
            if (document.activeElement instanceof HTMLElement && document.activeElement !== surface) {
              document.activeElement.blur();
            }
          });
        }, { signal });
      });

      dialogs.forEach(dialog => {
        dialog.addEventListener('keydown', event => {
          dialog.__pbnCloseModality = 'keyboard';
          if (event.key !== 'Escape') return;
          event.preventDefault();
          window.MyArtModalMotion ? window.MyArtModalMotion.close(dialog) : dialog.close();
        }, { signal });

        dialog.addEventListener('cancel', event => {
          event.preventDefault();
          dialog.__pbnCloseModality = 'keyboard';
          window.MyArtModalMotion ? window.MyArtModalMotion.close(dialog) : dialog.close();
        }, { signal });

        dialog.querySelectorAll('[data-pbn-main-dialog-close]').forEach(button => {
          button.addEventListener('click', event => {
            dialog.__pbnCloseModality = event.detail === 0 ? 'keyboard' : 'pointer';
            window.MyArtModalMotion ? window.MyArtModalMotion.close(dialog) : dialog.close();
          }, { signal });
        });

        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          dialog.__pbnCloseModality = 'pointer';
          window.MyArtModalMotion ? window.MyArtModalMotion.close(dialog) : dialog.close();
        }, { signal });

        dialog.addEventListener('close', () => {
          const opener = dialog.__pbnOpener;
          const closeModality = dialog.__pbnCloseModality || dialog.__pbnOpenModality;

          if (closeModality === 'keyboard') {
            if (opener instanceof HTMLElement && document.contains(opener)) opener.focus({ preventScroll: true });
          } else {
            if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
            if (opener instanceof HTMLElement) opener.blur();
            requestAnimationFrame(() => opener instanceof HTMLElement && opener.blur());
          }

          delete dialog.__pbnOpener;
          delete dialog.__pbnOpenModality;
          delete dialog.__pbnCloseModality;
          if (!dialogs.some(candidate => candidate.open)) this.unlockPageScroll();
        }, { signal });
      });

      this.classList.add('is-enhanced');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      updateAvailability();
    }

    lockPageScroll() {
      if (this.pageScrollLock) return;

      const html = document.documentElement;
      const body = document.body;
      const scrollY = window.scrollY;
      const viewportGutter = Math.max(0, window.innerWidth - body.getBoundingClientRect().width);
      const remember = (element, properties) => Object.fromEntries(properties.map(property => [property, element.style[property]]));

      this.pageScrollLock = {
        scrollY,
        html,
        body,
        htmlStyles: remember(html, ['overflow', 'overscrollBehavior', 'scrollBehavior']),
        bodyStyles: remember(body, ['position', 'top', 'left', 'right', 'width', 'overflow'])
      };

      html.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      html.style.scrollBehavior = 'auto';
      // Keep the desktop document and its sticky gallery in the same scroll coordinate system.
      body.style.width = `calc(100% - ${viewportGutter}px)`;
      if (!window.matchMedia('(min-width: 750px)').matches) {
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.overflow = 'hidden';
      }
    }

    unlockPageScroll() {
      const state = this.pageScrollLock;
      if (!state) return;

      Object.entries(state.htmlStyles).forEach(([property, value]) => { state.html.style[property] = value; });
      Object.entries(state.bodyStyles).forEach(([property, value]) => { state.body.style[property] = value; });
      window.scrollTo(0, state.scrollY);
      delete this.pageScrollLock;
    }

    disconnectedCallback() {
      this.querySelectorAll('dialog').forEach(dialog => { window.MyArtModalMotion?.cancel(dialog); if (dialog.open) dialog.close(); });
      this.controller?.abort();
      this.unlockPageScroll();
      delete this.controller;
    }
  }

  customElements.define(elementName, PbnMainOptions);
})();
